import { Alert, Button, Form, Input, Typography } from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ApiService from '../../core/service/ApiService';

const { Text } = Typography;

/**
 * Parola sıfırlama — giriş ekranının kanal seçici akışının aynısı:
 *
 *   1) kullanıcı adı            → ForgotPassword  (challenge alınır, KOD GÖNDERİLMEZ)
 *   2) kanal seçimi (SMS/mail)  → SendResetCode   (kod gönderilir, challenge yenilenir)
 *   3) kod + yeni parola        → ResetPassword
 *
 * Kod alanı sayaçtan BAĞIMSIZ görünür (sunucudaki kod ömrü 3 dk); sayaç yalnız "tekrar gönder"i kilitler.
 */
export default function ForgotPasswordForm({ onBack }) {
    const { t } = useTranslation();
    const [done, setDone] = useState(false);
    const [params, setParams] = useState({
        enabled: false,     // challenge alındı → kanal seçimi görünür
        sent: false,        // kod gönderildi   → kod + yeni parola alanları görünür
        key: null,
        timeout: 0,
        sms_auth: false,
        mail_auth: false,
        sms_text: '',
        mail_text: '',
    });

    const startTimer = () => {
        const interval = setInterval(() => {
            setParams((prev) => {
                if (prev.timeout - 1 === 0) clearInterval(interval);
                return { ...prev, timeout: prev.timeout - 1 };
            });
        }, 1000);
    };

    const sendCode = async (type) => {
        const res = await ApiService.Auth.SendResetCode({
            code_key: params.key,
            code_type: type,
        });

        if (res.success) {
            setParams((prev) => ({
                ...prev,
                sent: true,
                key: res.data?.access_token,    // her gönderimde challenge yenilenir
                timeout: 60,                    // yalnız "tekrar gönder" bekleme süresi
            }));
            startTimer();
        }
    };

    const submit = async (form) => {
        // 1. adım: kullanıcı adı
        if (!params.enabled) {
            const res = await ApiService.Auth.ForgotPassword({ username: form.username });

            if (res.success) {
                setParams({
                    enabled: true,
                    sent: false,
                    key: res.data?.access_token,
                    timeout: 0,
                    sms_auth: res.data?.sms_auth,
                    mail_auth: res.data?.mail_auth,
                    sms_text: res.data?.sms_text,
                    mail_text: res.data?.mail_text,
                });
            }
            return;
        }

        // 3. adım: kod + yeni parola
        const res = await ApiService.Auth.ResetPassword({
            code_key: params.key,
            code: form.code,
            newPassword: form.newPassword,
        });

        if (res.success) setDone(true);
    };

    if (done) {
        return (
            <>
                <Alert
                    type="success"
                    showIcon
                    message={t('Your password has been reset.')}
                    description={t('All of your other sessions have been signed out. You can log in with your new password.')}
                />
                <Button
                    type="primary"
                    block
                    style={{ height: 45, borderRadius: 8, marginTop: 16 }}
                    onClick={onBack}
                >
                    {t('Back to login')}
                </Button>
            </>
        );
    }

    return (
        <Form layout={'vertical'} autoComplete="off" onFinish={submit} size="large">
            <Form.Item
                name="username"
                rules={[{ required: true, message: t('Please input your username!') }]}
            >
                <Input
                    prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                    placeholder={t('Username')}
                    disabled={params.enabled}
                    style={{ borderRadius: 8 }}
                />
            </Form.Item>

            {params.enabled && (
                <>
                    <Form.Item style={{ marginBottom: 12 }}>
                        <div style={{ textAlign: 'center', marginBottom: 16 }}>
                            <Text>{t('Where should we send the reset code?')}</Text>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {params.sms_auth && (
                                <Button
                                    block
                                    disabled={params.timeout > 0}
                                    onClick={() => sendCode('sms')}
                                    style={{ borderRadius: 8 }}
                                >
                                    {t('SMS')} {` (*** *** **${params.sms_text})`}
                                </Button>
                            )}
                            {params.mail_auth && (
                                <Button
                                    block
                                    disabled={params.timeout > 0}
                                    onClick={() => sendCode('mail')}
                                    style={{ borderRadius: 8 }}
                                >
                                    {t('Mail')} {` (${params.mail_text})`}
                                </Button>
                            )}
                        </div>
                    </Form.Item>

                    {params.sent && (
                        <>
                            <Form.Item
                                name="code"
                                rules={[{ required: true, message: t('Please input sms verification code!') }]}
                            >
                                <Input
                                    placeholder={t('Verification Code')}
                                    maxLength={6}
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    addonAfter={params.timeout > 0 ? ` (${params.timeout})` : null}
                                    style={{ borderRadius: 8 }}
                                />
                            </Form.Item>

                            <Form.Item
                                name="newPassword"
                                rules={[
                                    { required: true, message: t('Please input your password!') },
                                    { min: 8, message: t('Password must be at least 8 characters.') },
                                ]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                                    placeholder={t('New password')}
                                    autoComplete="new-password"
                                    style={{ borderRadius: 8 }}
                                />
                            </Form.Item>
                        </>
                    )}
                </>
            )}

            {/* Kod gönderilmeden gönder butonu gösterilmez: kullanıcı önce kanal seçmeli. */}
            {(!params.enabled || params.sent) && (
                <Form.Item style={{ marginBottom: 0, marginTop: 12 }}>
                    <Button
                        type="primary"
                        htmlType="submit"
                        block
                        style={{ height: 45, borderRadius: 8, fontSize: 16, fontWeight: 500 }}
                    >
                        {!params.enabled ? t('Continue') : t('Reset password')}
                    </Button>
                </Form.Item>
            )}

            <div style={{ textAlign: 'center', marginTop: 8 }}>
                <Button type="link" onClick={onBack}>
                    {t('Back to login')}
                </Button>
            </div>
        </Form>
    );
}
